import { Link } from 'react-router-dom';
import Nav from '../components/Nav';
import Footer from '../components/Footer';

export default function ForRestaurants() {
  return (
    <>
      <Nav />
      <Hero />
      <NumbersStrip />
      <BenefitsGrid />
      <HowItWorks />
      <FAQ />
      <CTAStrip />
      <Footer />
    </>
  );
}

function Hero() {
  return (
    <section style={{ paddingTop: 48, paddingBottom: 56 }}>
      <div className="container">
        <div className="eyebrow" style={{ marginBottom: 24 }}>For restaurants & bars</div>
        <h1 className="display" style={{ fontSize: 'clamp(56px, 11vw, 144px)', margin: '0 0 32px', lineHeight: 0.92 }}>
          The corporate happy hour,{' '}
          <span className="serif-italic" style={{ color: 'var(--wine)' }}>captured.</span>
        </h1>
        <div className="forr-hero-row">
          <p style={{ fontSize: 'clamp(17px, 2vw, 22px)', lineHeight: 1.45, color: 'var(--ink-soft)', maxWidth: 620, margin: 0 }}>
            Pre-paid bookings from NYC office teams. Bigger groups, bigger tabs, and zero check-splitting drama. You host. Goaround sends the team and settles after.
          </p>
          <div className="forr-hero-ctas">
            <Link to="/onboarding" className="btn btn-pop">List your spot →</Link>
            <a href="#numbers" className="btn btn-ghost">See the numbers</a>
          </div>
        </div>
      </div>
      <style>{`
        .forr-hero-row { display: grid; grid-template-columns: 1.2fr 1fr; gap: 48px; align-items: end; margin-top: 24px; }
        .forr-hero-ctas { display: flex; flex-wrap: wrap; gap: 12px; justify-content: flex-end; }
        @media (max-width: 720px) {
          .forr-hero-row { grid-template-columns: 1fr; gap: 24px; }
          .forr-hero-ctas { flex-direction: column; align-items: stretch; }
          .forr-hero-ctas .btn { width: 100%; justify-content: center; }
        }
      `}</style>
    </section>
  );
}

function NumbersStrip() {
  // Headline numbers up front. These are observed averages on the platform,
  // not commitments to any specific bar; the page deliberately sells outcome
  // without guaranteeing it.
  const stats = [
    { v: '$540',   l: 'Average pre-paid tab' },
    { v: '12',     l: 'Avg guests per booking' },
    { v: '$45',    l: 'Pre-paid spend per head' },
    { v: 'Tue–Thu', l: 'When bookings concentrate' },
  ];
  return (
    <section id="numbers" className="section">
      <div className="container">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'end', marginBottom: 32, gap: 24, flexWrap: 'wrap' }}>
          <div>
            <div className="eyebrow" style={{ marginBottom: 16 }}>How it shakes out</div>
            <h2 className="display" style={{ fontSize: 'clamp(36px, 5.4vw, 64px)', margin: 0, maxWidth: 760 }}>
              The bookings aren't <span className="serif-italic" style={{ color: 'var(--wine)' }}>walk-in size.</span>
            </h2>
          </div>
          <p style={{ color: 'var(--ink-soft)', fontSize: 15, margin: 0, maxWidth: 360 }}>
            Observed averages on the platform. Your numbers will move with your room, your hours, and the package you set.
          </p>
        </div>
        <div className="r-grid-4" style={{ borderTop: '1.5px solid var(--ink)', paddingTop: 28, gap: 24 }}>
          {stats.map((s) => (
            <div key={s.l}>
              <div className="display" style={{ fontSize: 'clamp(40px, 5.6vw, 80px)', letterSpacing: '-0.04em' }}>{s.v}</div>
              <div className="mono" style={{ fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ink-soft)', marginTop: 8 }}>{s.l}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function BenefitsGrid() {
  const benefits = [
    {
      tag: 'Demand',
      t: 'Corporate teams with budget.',
      b: 'NYC offices booking team nights on the company card. Not bachelorettes, not tourists, not Friday-night walk-ins looking for a deal.',
    },
    {
      tag: 'Tab',
      t: 'Pre-paid before they sit.',
      b: "Goaround charges the company up front. You see the booking with a real headcount and a real total. Settlement lands in your account net 3.",
    },
    {
      tag: 'Headcount',
      t: 'Locked by 3pm day-of.',
      b: "Final count is final. Staff and stock to the actual number, not the optimistic one. No 'we said 20 but only 12 showed.'",
    },
    {
      tag: 'Control',
      t: 'Your offer, your math.',
      b: "Pick from three tier presets, tune the price per head, set the days you can host. Block dates when you can't. Pause the listing anytime.",
    },
    {
      tag: 'Upside',
      t: "Extras still ring on your POS.",
      b: 'Guests routinely add rounds on their own card once the package runs out. That tab is yours, billed by you, like any walk-in.',
    },
    {
      tag: 'Zero ops',
      t: 'Nothing new to log into.',
      b: "We handle invites, RSVPs, reminders, payments. You get an email with the booking and the headcount. The OpenTable for groups, without the host stand.",
    },
  ];
  return (
    <section className="section">
      <div className="container">
        <div style={{ marginBottom: 48, maxWidth: 880 }}>
          <div className="eyebrow" style={{ marginBottom: 18 }}>What you get</div>
          <h2 className="display" style={{ fontSize: 'clamp(40px, 5.2vw, 76px)', margin: 0 }}>
            Higher-ACV groups, <span className="serif-italic" style={{ color: 'var(--wine)' }}>fewer headaches.</span>
          </h2>
        </div>
        <div className="r-grid-3">
          {benefits.map((b) => (
            <div key={b.tag} style={{ padding: 24, borderRadius: 18, background: 'var(--card)', border: '1px solid var(--line)' }}>
              <span className="mono" style={{ fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', padding: '6px 10px', border: '1px solid var(--ink)', borderRadius: 999 }}>{b.tag}</span>
              <h3 className="display" style={{ fontSize: 22, margin: '20px 0 10px' }}>{b.t}</h3>
              <p style={{ color: 'var(--ink-soft)', fontSize: 15, margin: 0, lineHeight: 1.5 }}>{b.b}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  const steps = [
    { n: '01', tag: 'Set up',         title: 'List your spot.',     body: 'Two screens. Address, contact, then pick a tier (Starter / Standard / Premium), set your price per head, name the days you can host.' },
    { n: '02', tag: 'Review',         title: 'Live the same day.',  body: 'A human at Goaround reviews your listing in a few hours. We email the moment it goes live. Most spots are taking bookings inside a week.' },
    { n: '03', tag: 'Take bookings',  title: 'Confirm by 3pm.',     body: 'We email each booking with the date, time, and headcount. You confirm by 3pm day-of. Team shows up pre-paid. Settlement net 3.' },
  ];
  return (
    <section id="how" className="section">
      <div className="container">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'end', marginBottom: 48, gap: 48, flexWrap: 'wrap' }}>
          <div>
            <div className="eyebrow" style={{ marginBottom: 18 }}>How it works</div>
            <h2 className="display" style={{ fontSize: 'clamp(40px, 5.2vw, 76px)', margin: 0 }}>
              Listed today. <span className="serif-italic" style={{ color: 'var(--wine)' }}>Taking bookings by Friday.</span>
            </h2>
          </div>
        </div>
        <div className="r-grid-3" style={{ borderTop: '1.5px solid var(--ink)', gap: 0 }}>
          {steps.map((s, i) => (
            <div key={s.n} style={{ padding: '24px 20px 32px', borderRight: i === steps.length - 1 ? 'none' : '1px solid var(--line)', borderBottom: '1px solid var(--line)', minHeight: 220 }}>
              <div className="display" style={{ fontSize: 48, color: 'var(--primary)', lineHeight: 1 }}>{s.n}</div>
              <span className="mono" style={{ marginTop: 12, display: 'inline-block', fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', padding: '6px 10px', border: '1px solid var(--ink)', borderRadius: 999 }}>{s.tag}</span>
              <h3 className="display" style={{ fontSize: 24, margin: '20px 0 10px' }}>{s.title}</h3>
              <p style={{ color: 'var(--ink-soft)', fontSize: 15, margin: 0, lineHeight: 1.55 }}>{s.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQ() {
  const qs = [
    { q: 'When do we get paid?', a: 'Goaround charges the booking organizer up front, then settles with you by direct deposit on net 3 business days after the event runs.' },
    { q: "What's the take rate?", a: 'A small platform fee on the package subtotal. We share specifics on the intro call after your application. Tax and gratuity always pass through.' },
    { q: 'Do we have to accept every booking?', a: "No. Each booking pings you for confirmation. You can decline a date that doesn't work for you, and that night just doesn't go on the calendar." },
    { q: 'What if a team cancels?', a: 'Cancellations and headcount changes are allowed up to 3pm day-of. After that, the package transfers to a new date or you keep the deposit per your policy.' },
    { q: 'Blackout dates and minimums?', a: 'Yes. The vendor dashboard has a calendar. Block dates, change minimums, pause your offer anytime.' },
    { q: 'Do guests still order off the package?', a: 'Yes, and you keep all of it. They add rounds at the bar on their own card once the pre-paid package runs. Same as any walk-in.' },
    { q: 'What kind of groups book?', a: "Mostly NYC office teams (8 to 30 people) running a team night or a client event. Tue, Wed, and Thu are where most bookings land. Average pre-paid tab right now is around $540." },
    { q: "What's the catch?", a: "There isn't one we hide. The numbers on this page are observed averages on the platform, not promises. Your room and your offer will move them up or down." },
  ];
  return (
    <section className="section">
      <div className="container">
        <div className="r-split-narrow">
          <div style={{ position: 'sticky', top: 100 }}>
            <div className="eyebrow" style={{ marginBottom: 16 }}>FAQ</div>
            <h2 className="display" style={{ fontSize: 'clamp(32px, 4.5vw, 60px)', margin: 0 }}>
              The <span className="serif-italic" style={{ color: 'var(--wine)' }}>real</span> questions.
            </h2>
            <p style={{ color: 'var(--ink-soft)', marginTop: 14, fontSize: 15 }}>
              Email <a href="mailto:hi@goaround.io">hi@goaround.io</a> for anything else.
            </p>
          </div>
          <div>
            {qs.map((item, i) => (
              <details key={i} style={{ borderTop: i === 0 ? '1.5px solid var(--ink)' : '1px solid var(--line)', padding: '20px 0' }}>
                <summary style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16 }}>
                  <span className="display" style={{ fontSize: 20 }}>{item.q}</span>
                  <span className="faq-icon display" style={{ fontSize: 26, color: 'var(--primary)' }}>+</span>
                </summary>
                <p style={{ color: 'var(--ink-soft)', fontSize: 15, lineHeight: 1.55, marginTop: 12, marginBottom: 0, maxWidth: 640 }}>{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function CTAStrip() {
  return (
    <section style={{ padding: 'clamp(72px, 12vw, 120px) 0', background: 'var(--ink)', color: 'var(--bg)', position: 'relative', overflow: 'hidden' }}>
      <div aria-hidden className="display" style={{ position: 'absolute', right: -60, top: -40, fontSize: 'clamp(280px, 50vw, 540px)', color: 'color-mix(in oklch, var(--primary) 28%, transparent)', lineHeight: 0.8, pointerEvents: 'none' }}>G</div>
      <div className="container" style={{ position: 'relative' }}>
        <div style={{ maxWidth: 900 }}>
          <div className="eyebrow" style={{ marginBottom: 18, color: 'var(--primary)' }}>Get on the network</div>
          <h2 className="display" style={{ fontSize: 'clamp(40px, 7vw, 96px)', margin: 0 }}>
            Two screens to <span className="serif-italic" style={{ color: 'var(--pop)' }}>list your spot.</span>
          </h2>
          <p style={{ fontSize: 'clamp(16px, 2vw, 19px)', color: 'color-mix(in oklch, var(--bg) 75%, transparent)', marginTop: 20, maxWidth: 620 }}>
            Sign up, build your offer, get reviewed today. The first corporate group is usually inside a week.
          </p>
        </div>
        <div className="forr-cta-buttons">
          <Link to="/onboarding" className="btn btn-pop">Sign up & create your offer →</Link>
          <Link to="/featured-locations" className="btn btn-ghost" style={{ borderColor: 'var(--bg)', color: 'var(--bg)' }}>See live listings</Link>
        </div>
        <div className="mono" style={{ marginTop: 22, fontSize: 12, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'color-mix(in oklch, var(--bg) 55%, transparent)' }}>
          Free to list · Reviewed same day · Manhattan only (for now)
        </div>
      </div>
      <style>{`
        .forr-cta-buttons { margin-top: 32px; display: flex; gap: 12px; flex-wrap: wrap; }
        @media (max-width: 720px) {
          .forr-cta-buttons { flex-direction: column; align-items: stretch; }
          .forr-cta-buttons .btn { width: 100%; justify-content: center; }
        }
      `}</style>
    </section>
  );
}
